import {
  InfiniteData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ApiArchiveFilterParams,
  ApiArchiveTimeSeriesData,
  ApiArchiveTimeSeriesParams,
  ApiBulkRoutePatch,
  ApiCircuit,
  ApiCreateLinePayload,
  ApiCreateZonePayload,
  ApiLine,
  ApiRouteAttempt,
  ApiRouteCustomScoringConfig,
  ApiRouteDetail,
  ApiRoutePayload,
  ApiRoutesFacets,
  ApiRouteStats,
  ApiUpdateLinePayload,
  ApiUpdateZonePayload,
  ApiUserInline,
  ApiZone,
  ClimbType,
  PaginatedResponse,
} from "griptonite-api-client";
import { HTTPError } from "griptonite-api-client/client";
import { groupBy, orderBy } from "lodash";
import { useCallback, useMemo } from "react";

import { useRoutesUiStore } from "@/components/RoutesProvider";
import { TimeSeriesData } from "@/components/TimeSeriesGraph/TimeSeriesGraph";
import queryClient from "@/core/queryClient";

import api from "../core/api";
import { Route } from "../core/types";
import { useCompetitionQuery } from "./competitions";
import { useCurrentClimbType, useCurrentVenueQuery, useCurrentVenueUuid } from "./venues";

export const useVenueRoutesQuery = <TData = Route[]>(
  uuid?: string,
  select?: (data: Route[]) => TData,
) =>
  useQuery({
    queryKey: ["venues", uuid, "routes"],
    queryFn: async () => {
      const result = await api.venues.getVenueRoutes(uuid!);
      return result.data;
    },
    enabled: !!uuid,
    select,
    gcTime: 1000 * 60 * 30,
  });

export const useVenueRoutesByClimbTypeQuery = (uuid?: string, climbType?: string) => {
  const select = useCallback(
    (data: Route[]) => data.filter((route) => route.climbType === climbType),
    [climbType],
  );
  return useVenueRoutesQuery(uuid, select);
};

export const useCompetitionRoutesQuery = <TData = Route[]>(
  competitionUuid: string | undefined,
  venueUuid: string | undefined,
  select?: (data: Route[]) => TData,
) => {
  return useQuery({
    queryKey: ["venues", venueUuid, "competitionRoutes", competitionUuid],
    queryFn: async () => {
      const result = await api.venues.getVenueRoutes(venueUuid!, {
        competitionUuid,
      });
      return result.data;
    },
    enabled: !!competitionUuid && !!venueUuid,
    select,
    refetchOnMount: "always",
  });
};

export const useVenueCircuitsQuery = <TData = ApiCircuit[]>(
  uuid?: string,
  select?: (data: ApiCircuit[]) => TData,
) =>
  useQuery({
    queryKey: ["venues", uuid, "circuits"],
    queryFn: async () => {
      const result = await api.venues.getVenueCircuits(uuid!);
      return result.data;
    },
    select,
    enabled: !!uuid,
  });

export const useVenueLinesQuery = <TData = ApiLine[]>(
  uuid?: string,
  select?: (data: ApiLine[]) => TData,
) =>
  useQuery({
    queryKey: ["venues", uuid, "lines"],
    queryFn: async () => {
      const result = await api.venues.getVenueLines(uuid!);
      return result.data;
    },
    select,
    enabled: !!uuid,
  });

export const useVenueCircuitsByUuid = (uuid?: string) => {
  const select = useCallback(
    (data: ApiCircuit[]) => Object.fromEntries(data.map((circuit) => [circuit.uuid, circuit])),
    [],
  );
  const circuits = useVenueCircuitsQuery(uuid, select);
  return circuits.data;
};

export const useVenueLinesByUuid = (uuid?: string) => {
  const select = useCallback(
    (data: ApiLine[]) => Object.fromEntries(data.map((line) => [line.uuid, line])),
    [],
  );
  const lines = useVenueLinesQuery(uuid, select);
  return lines.data;
};

export const useVenueRoutesByUuid = (uuid?: string) => {
  const select = useCallback(
    (data: Route[]) => Object.fromEntries(data.map((route) => [route.uuid, route])),
    [],
  );
  const routes = useVenueRoutesQuery(uuid, select);
  return routes.data;
};

// export const useVenueZonesQuery = (uuid?: string) => {
export const useVenueZonesQuery = <TData = ApiZone[]>(
  uuid?: string,
  select?: (data: ApiZone[]) => TData,
) => {
  return useQuery({
    queryKey: ["venues", uuid, "zones"],
    queryFn: async () => {
      const result = await api.venues.getVenueZones(uuid!);
      return result.data;
    },
    select,
    enabled: !!uuid,
  });
};

export const useVenueZonesByClimbType = (uuid?: string, climbType?: ClimbType) => {
  const select = useCallback(
    (data: ApiZone[]) =>
      data
        .filter((zone) => zone.climbType === climbType)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
          }),
        ),
    [climbType],
  );
  return useVenueZonesQuery(uuid, select);
};

export const useVenueZonesByUuid = (uuid?: string, climbType?: ClimbType) => {
  const select = useCallback(
    (data: ApiZone[]) => {
      if (climbType) {
        data = data.filter((zone) => zone.climbType === climbType);
      }
      return Object.fromEntries(data.map((zone) => [zone.uuid, zone]));
    },
    [climbType],
  );
  const zones = useVenueZonesQuery(uuid, select);
  return zones.data;
};

export const useCircuitQuery = (uuid?: string, venueUuid?: string) => {
  return useQuery({
    queryKey: ["circuits", uuid],
    queryFn: () => api.routes.getCircuit(uuid!),
    enabled: !!uuid,
    initialData: () => {
      const circuits = queryClient.getQueryData<ApiCircuit[]>(["venues", venueUuid, "circuits"]);
      return circuits?.find((circuit) => circuit.uuid === uuid);
    },
  });
};

export const useLineQuery = (uuid?: string, venueUuid?: string) => {
  return useQuery({
    queryKey: ["lines", uuid],
    queryFn: () => api.routes.getLine(uuid!),
    enabled: !!uuid,
    initialData: () => {
      const lines = queryClient.getQueryData<ApiLine[]>(["venues", venueUuid, "lines"]);
      return lines?.find((line) => line.uuid === uuid);
    },
  });
};

export const useCurrentVenueCircuitLookup = (uuid?: string) => {
  const currentVenueUuid = useCurrentVenueUuid();
  return useCircuitQuery(uuid, currentVenueUuid).data;
};

export const useCurrentVenueLineLookup = (uuid?: string) => {
  const currentVenueUuid = useCurrentVenueUuid();
  return useLineQuery(uuid, currentVenueUuid).data;
};

export const useCurrentVenueCompetitionLookup = (uuid?: string) => {
  const currentVenueUuid = useCurrentVenueUuid();
  return useCompetitionQuery(uuid, currentVenueUuid).data;
};

export const useCurrentVenueRouteLookup = (uuid?: string) => {
  const currentVenueUuid = useCurrentVenueUuid();
  const routesByUuid = useVenueRoutesByUuid(currentVenueUuid);
  return uuid ? routesByUuid?.[uuid] : undefined;
};

export const useCompetitionRoutesByUuid = (competitionUuid?: string, venueUuid?: string) => {
  const select = useCallback(
    (data: Route[]) => Object.fromEntries(data.map((route) => [route.uuid, route])),
    [],
  );
  return useCompetitionRoutesQuery(competitionUuid, venueUuid, select);
};

export const useCompetitionRouteLookup = (competitionUuid?: string, routeUuid?: string) => {
  const venueUuid = useCurrentVenueUuid();
  const { data: routesByUuid } = useCompetitionRoutesByUuid(competitionUuid, venueUuid);
  return routeUuid ? routesByUuid?.[routeUuid] : undefined;
};

const replaceRouteInArray = (
  routes: Route[] | undefined,
  uuid: string,
  newRoute: Route,
): Route[] => {
  if (!routes) {
    return [newRoute];
  }
  const index = routes.findIndex((r) => r.uuid === uuid);
  if (index === -1) {
    return [...routes, newRoute];
  }
  return [...routes.slice(0, index), newRoute, ...routes.slice(index + 1)];
};

export const useUpdateRouteMutation = (uuid?: string) => {
  const venueUuid = useCurrentVenueUuid();
  const queryClient = useQueryClient();
  const { clearMapPositionEdit } = useRoutesUiStore();

  return useMutation({
    mutationFn: (routeData: Partial<ApiRoutePayload>) => {
      if (!uuid) {
        throw new Error("Route must have a uuid to update");
      }
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      return api.routes.updateRoute(uuid, routeData);
    },
    onSuccess: (data) => {
      if (!uuid) {
        throw new Error("Route must have a uuid to update");
      }
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      queryClient.setQueryData(["venues", venueUuid, "routes"], (existingData: Route[]) =>
        replaceRouteInArray(existingData, data.uuid, data),
      );
      queryClient.setQueriesData(
        {
          queryKey: ["venues", venueUuid, "competitionRoutes"],
        },
        (existingData: Route[] | undefined) => replaceRouteInArray(existingData, data.uuid, data),
      );

      clearMapPositionEdit(uuid);
      queryClient.invalidateQueries({
        queryKey: ["venues", venueUuid, "tasksets"],
      });
    },
  });
};

export const useCreateRouteMutation = () => {
  const venueUuid = useCurrentVenueUuid();
  const climbType = useCurrentClimbType();
  const queryClient = useQueryClient();
  const { data: venue } = useCurrentVenueQuery();

  return useMutation({
    mutationFn: (routeData?: Partial<ApiRoutePayload> & { uuid?: string }) => {
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      if (!climbType) {
        throw new Error("Climb type must be set");
      }
      // remove the temporary uuid, if it has one
      const data = { ...routeData };
      delete data["uuid"];
      return api.routes.createRoute({
        gradeSystem:
          climbType === "BOULDER"
            ? venue?.boulderGradeSystem || "FONT"
            : venue?.sportGradeSystem || "FRENCH",
        ...data,
        venue: venueUuid,
        climbType,
      });
    },
    onSuccess: (data) => {
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      queryClient.setQueryData(["venues", venueUuid, "routes"], (existingData: Route[]) => [
        ...existingData,
        data,
      ]);
      queryClient.invalidateQueries({
        queryKey: ["venues", venueUuid, "tasksets"],
      });
    },
  });
};

export const useBulkUpdateRoutesMutation = () => {
  const queryClient = useQueryClient();
  const venueUuid = useCurrentVenueUuid();

  return useMutation({
    mutationFn: (updates: ApiBulkRoutePatch[]) => {
      if (!venueUuid) {
        throw new Error("venueUuid must be set!");
      }
      return api.routes.updateRoutes(updates);
    },
    onSuccess: (data) => {
      if (!venueUuid) {
        throw new Error("venueUuid must be set!");
      }
      queryClient.setQueryData(["venues", venueUuid, "routes"], (existingData: Route[]) => {
        let finalList = existingData;
        data.forEach((route) => (finalList = replaceRouteInArray(finalList, route.uuid, route)));
        return finalList;
      });
      queryClient.invalidateQueries({
        queryKey: ["venues", venueUuid, "tasksets"],
      });
    },
  });
};

export const useStripRoutesMutation = () => {
  const queryClient = useQueryClient();
  const venueUuid = useCurrentVenueUuid();

  return useMutation({
    mutationFn: ({ routeUuids, endTime = "NOW" }: { routeUuids: string[]; endTime?: string }) => {
      if (!venueUuid) {
        throw new Error("venueUuid must be set!");
      }
      const timePatch = endTime === "NOW" ? { endTime: "NOW" } : { scheduledEndTime: endTime };
      const patches = routeUuids.map((uuid) => ({
        uuid,
        ...timePatch,
      }));
      return api.routes.updateRoutes(patches);
    },
    onSuccess: (_, { routeUuids, endTime }) => {
      if (!venueUuid) {
        throw new Error("venueUuid must be set!");
      }
      if (endTime === "NOW") {
        queryClient.setQueryData(["venues", venueUuid, "routes"], (existingData: Route[]) =>
          existingData.filter((route) => !routeUuids.includes(route.uuid)),
        );
      }
      queryClient.invalidateQueries({
        queryKey: ["venues", venueUuid, "tasksets"],
      });
      // Stripping creates a stripping task, so an open task set needs to
      // refetch for the new card to appear.
      queryClient.invalidateQueries({
        queryKey: ["tasksets"],
      });
    },
  });
};

const emptyList = [] as never[];

export const useRoutesByUuids = (selectedRouteUuids: Iterable<string>) => {
  const currentVenueUuid = useCurrentVenueUuid();
  const routesByUuid = useVenueRoutesByUuid(currentVenueUuid);

  return useMemo(() => {
    if (!routesByUuid) {
      return emptyList;
    }
    return Array.from(selectedRouteUuids, (uuid) => routesByUuid[uuid]).filter(Boolean);
  }, [selectedRouteUuids, routesByUuid]);
};

export const useExportRoutesMutation = () => {
  const venueUuid = useCurrentVenueUuid();
  return useMutation({
    mutationFn: async (routeUuids: string[]) => {
      if (!venueUuid) {
        throw new Error("venueUuid must be set!");
      }
      const response = await api.venues.exportVenueRoutes(venueUuid, routeUuids);
      const header = response.headers.get("Content-Disposition");
      if (!header) {
        throw new Error("No filename in response");
      }
      const filename = header.split("filename=")[1];
      return { filename, blob: await response.blob() };
    },
  });
};

export const useVenueRouteStatsQuery = <TData>(
  uuid?: string,
  select?: (data: ApiRouteStats[]) => TData,
) => {
  return useQuery({
    queryKey: ["venues", uuid, "routeStats"],
    queryFn: () => api.venues.getVenueRoutesStats(uuid!),
    enabled: !!uuid,
    select,
  });
};

export const useVenueRouteStatsByUuid = (uuid?: string) => {
  const select = useCallback((stats: ApiRouteStats[]) => {
    return Object.fromEntries(stats.map((stat) => [stat.uuid, stat]));
  }, []);
  return useVenueRouteStatsQuery(uuid, select);
};

export const useVenueRouteSettersByUuid = (uuid?: string) => {
  const select = useCallback((routes: Route[]) => {
    const items = {} as Record<string, ApiUserInline>;
    routes.forEach((route) => {
      route.setters.forEach((setter) => {
        if (items[setter.uuid]) {
          return;
        }
        items[setter.uuid] = setter;
      });
    });
    return items;
  }, []);
  return useVenueRoutesQuery(uuid, select).data;
};

export const routeAttemptsByUuidQuery = (uuid?: string, competitionOnly?: boolean) => {
  return queryOptions({
    queryKey: ["routes", uuid, competitionOnly ? "competitionAttempts" : "attempts"],
    queryFn: async () => {
      try {
        return await api.collectPages(() => api.routes.getAllRouteAttempts(uuid!, competitionOnly));
      } catch (err) {
        if (err instanceof HTTPError && err.response.status === 404) {
          return [];
        } else {
          throw err;
        }
      }
    },
    enabled: !!uuid,
  });
};

export const useRouteAttemptsByUuid = (uuid?: string, competitionOnly?: boolean) => {
  return useQuery(routeAttemptsByUuidQuery(uuid, competitionOnly));
};

export const useRouteAttemptsByUuidGrouped = (uuid?: string, competitionOnly?: boolean) => {
  const select = useCallback((attempts: (ApiRouteAttempt & { user: string })[]) => {
    return orderBy(
      groupBy(attempts.reverse(), (attempt) => attempt.user),
      (group) => group[group.length - 1].timestamp,
      "desc",
    );
  }, []);
  return useQuery({
    ...routeAttemptsByUuidQuery(uuid, competitionOnly),
    select,
  });
};

export const useCreateZoneMutation = () => {
  const venueUuid = useCurrentVenueUuid();

  return useMutation({
    mutationFn: (zoneData: ApiCreateZonePayload) => {
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      return api.routes.createZone(venueUuid, zoneData);
    },
    onSuccess: (data) => {
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      queryClient.setQueryData(["venues", venueUuid, "zones"], (existingData: ApiZone[]) => [
        ...existingData,
        data,
      ]);
    },
  });
};

export const useUpdateZoneMutation = () => {
  const venueUuid = useCurrentVenueUuid();

  return useMutation({
    mutationFn: (payload: ApiUpdateZonePayload & { uuid: string }) => {
      const { uuid, ...zoneData } = payload;

      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      return api.routes.updateZone(venueUuid, uuid, zoneData);
    },
    onSuccess: (zone) => {
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      queryClient.setQueryData(["venues", venueUuid, "zones"], (existingData: ApiZone[]) =>
        existingData.map((existingZone) => (existingZone.uuid === zone.uuid ? zone : existingZone)),
      );
    },
  });
};

export const useDeleteZoneMutation = () => {
  const venueUuid = useCurrentVenueUuid();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid: string) => {
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      return api.routes.deleteZone(venueUuid, uuid);
    },
    onSuccess: (_, uuid) => {
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      queryClient.setQueryData(["venues", venueUuid, "zones"], (existingData: ApiZone[]) =>
        existingData.filter((zone) => zone.uuid !== uuid),
      );
    },
  });
};

export const useUpdateLineMutation = () => {
  const queryClient = useQueryClient();
  const venueUuid = useCurrentVenueUuid();
  return useMutation({
    mutationFn: (payload: ApiUpdateLinePayload & { uuid: string }) => {
      const { uuid, ...lineData } = payload;
      return api.routes.updateLine(uuid, lineData);
    },
    onSuccess: (line) => {
      if (venueUuid) {
        queryClient.setQueryData(["venues", venueUuid, "lines"], (existingData: ApiZone[]) =>
          existingData.map((existingLine) =>
            existingLine.uuid === line.uuid ? line : existingLine,
          ),
        );
      }
      queryClient.setQueryData(["lines", line.uuid], line);
    },
  });
};

export const useCreateLineMutation = () => {
  const queryClient = useQueryClient();
  const venueUuid = useCurrentVenueUuid();
  return useMutation({
    mutationFn: (lineData: ApiCreateLinePayload) => {
      return api.routes.createLine(lineData);
    },
    onSuccess: (line) => {
      if (venueUuid) {
        queryClient.setQueryData(["venues", venueUuid, "lines"], (existingData: ApiZone[]) => [
          ...existingData,
          line,
        ]);
      }
      queryClient.setQueryData(["lines", line.uuid], line);
    },
  });
};

export const useOptimisticUpdateLineZoneMutation = () => {
  const queryClient = useQueryClient();
  const venueUuid = useCurrentVenueUuid();
  return useMutation({
    mutationFn: ({ uuid, zone }: { uuid: string; zone: { uuid: string; name: string } | null }) => {
      const lineData = {
        zone: zone?.uuid ?? null,
      };
      return api.routes.updateLine(uuid, lineData);
    },
    onMutate: ({ uuid, zone }) => {
      if (venueUuid) {
        queryClient.setQueryData(["venues", venueUuid, "lines"], (existingData: ApiZone[]) =>
          existingData.map((existingLine) =>
            existingLine.uuid === uuid
              ? {
                  ...existingLine,
                  zone,
                }
              : existingLine,
          ),
        );
      }
      queryClient.setQueryData(["lines", uuid], (existingData: ApiZone) => ({
        ...existingData,
        zone,
      }));
    },
    // todo: onError
  });
};

export const useDeleteLineMutation = () => {
  const queryClient = useQueryClient();
  const venueUuid = useCurrentVenueUuid();

  return useMutation({
    mutationFn: (uuid: string) => {
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      return api.routes.deleteLine(uuid);
    },
    onSuccess: (_, uuid) => {
      if (!venueUuid) {
        throw new Error("Venue uuid must be set");
      }
      queryClient.setQueryData(["venues", venueUuid, "lines"], (existingData: ApiLine[]) =>
        existingData.filter((line) => line.uuid !== uuid),
      );
      queryClient.setQueryData(["lines", uuid], null);
    },
  });
};

export const useVenueArchiveRoutesByClimbTypeQuery = <
  TData = InfiniteData<PaginatedResponse<ApiRouteDetail>>,
>(
  venueUuid?: string,
  climbType?: ClimbType,
  filterParams?: ApiArchiveFilterParams,
  select?: (data: InfiniteData<PaginatedResponse<ApiRouteDetail>>) => TData,
) => {
  const params = { ...filterParams, climbType };
  return useInfiniteQuery({
    queryKey: ["venues", venueUuid, "archivedRoutes", params],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const result = await api.venues.getVenueArchiveRoutes(venueUuid!, {
        page: pageParam,
        ...params,
      });
      return result;
    },
    getNextPageParam: (lastPage: PaginatedResponse<ApiRouteDetail>) => {
      const nextPageUrl = lastPage.metadata.next;
      if (!nextPageUrl) {
        return undefined;
      }
      const url = new URL(nextPageUrl, location.origin);
      const page = url.searchParams.get("page");
      return page ? parseInt(page, 10) : 1;
    },
    enabled: !!venueUuid,
    select,
    gcTime: 1000 * 30,
  });
};

export const useVenueArchiveRoutesFacetsQuery = <TData = ApiRoutesFacets>(
  venueUuid?: string,
  climbType?: ClimbType,
  filterParams?: ApiArchiveFilterParams,
  select?: (data: ApiRoutesFacets) => TData,
) => {
  const params = { ...filterParams, climbType };
  const result = useQuery({
    queryKey: ["venues", venueUuid, "archivedRoutesFacets", params],
    queryFn: () => {
      return api.venues.getVenueArchiveRoutesFacets(venueUuid!, params);
    },
    enabled: !!venueUuid && !!climbType,
    select,
  });
  return result;
};

function convertTimeSeriesDates(inputData: ApiArchiveTimeSeriesData): TimeSeriesData {
  return {
    ...inputData,
    columns: inputData.columns.map((column) => ({
      ...column,
      data: column.data.map(({ x, y }) => ({
        x: new Date(x),
        y,
      })),
    })),
    totals: inputData.totals.map(({ x, y }) => ({
      x: new Date(x),
      y,
    })),
    totalsCompare: inputData.totalsCompare?.map(({ x, y }) => ({
      x: new Date(x),
      y,
    })),
  };
}

export const useVenueArchiveRoutesTimeSeriesQuery = (
  venueUuid?: string,
  climbType?: ClimbType,
  params?: ApiArchiveTimeSeriesParams,
  filterParams?: ApiArchiveFilterParams,
) => {
  return useQuery({
    queryKey: [
      "venues",
      venueUuid,
      "archivedRoutesTimeSeries",
      climbType,
      { ...params, ...filterParams },
    ],
    queryFn: async () => {
      return api.venues.getVenueArchiveRoutesTimeSeries(venueUuid!, params!, {
        ...filterParams,
        climbType,
      });
    },
    enabled: !!venueUuid && !!climbType && !!params,
    gcTime: 1000 * 30,
    select: convertTimeSeriesDates,
  });
};

export const useRouteCustomScoringConfigQuery = (routeUuid?: string) => {
  return useQuery({
    queryKey: ["routes", routeUuid, "customScoringConfig"],
    queryFn: () => api.routes.getRouteCustomScoringConfig(routeUuid!),
    enabled: !!routeUuid,
    gcTime: 1000,
  });
};

export const useUpdateRouteCustomScoringConfigMutation = (competitionUuid?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ApiRouteCustomScoringConfig & { routeUuid: string }) => {
      const { routeUuid, ...payload } = data;
      return api.routes.updateRouteCustomScoringConfig(routeUuid, payload);
    },
    onSuccess: (result, { routeUuid }) => {
      queryClient.setQueryData(["routes", routeUuid, "customScoringConfig"], result);
      queryClient.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "competitions" && q.queryKey[2] === "routeCustomScoringConfigs",
      });
      if (competitionUuid) {
        queryClient.invalidateQueries({
          queryKey: ["competitions", competitionUuid, "entries"],
        });
      }
    },
  });
};
